import { AxiosError } from "axios";
import { AddNewBrandForm } from "../components/AddBrandModal";
import { useBrandStore } from "../store/useBrandStore";
import { useMessagesStore } from "../store/useMessagesStore";
import { useStatisticsStore } from "../store/useStatisticsStore";
import { Brand, BrandStatus, BrandThemeObject } from "../types/brand";
import { BrandUser } from "../types/user";
import { unmaskPhone } from "../utils";
import api, { publicApi } from "./api";

const { addError } = useMessagesStore.getState();
const { setBrands, setBrandIndustries, addNewBrand } = useBrandStore.getState();
const { setUsersStatistics } = useStatisticsStore.getState();

export const apiGetBrandsList = async (
  useHierarchy: boolean = true
) => {
  try {
    const result = await api.get<Brand[]>(
      `/brand/list?useHierarchy=${useHierarchy}`
    );
    setBrands(result.data);
    return result.data;
  } catch (error) {
    addError('Failed to fetch list of brands');
    return null;
  }
}

export const apiGetBrandIndustries = async () => {
  try {
    const result = await api.get<string[]>('/brand/industries');
    setBrandIndustries(result.data);
  } catch (error) {
    addError('Failed to fetch list of brand industries');
  }
}

export const apiAddNewBrand = async (values: AddNewBrandForm) => {
  try {
    if (values.adminPhone) {
      values.adminPhone = unmaskPhone(values.adminPhone);
    }

    // Keep backend contract stable: always send industries as a (single-item) array
    const industries =
      Array.isArray(values.industries)
        ? values.industries
        : values.industries
          ? [values.industries]
          : [];

    const formData = new FormData();
    Object.entries(values).forEach(([key, value]) => {
      if (value) {
        if (key === 'adminPhone') {
          formData.append(key, unmaskPhone(value))
        } else if (key === 'industries') {
          // Append each industry separately so backend receives an array (even if there's only one)
          industries.forEach((industry) => formData.append('industries', industry));
        } else {
          formData.append(key, value);
        }
      }

    })

    const result = await api.post<Brand>('/brand', formData, {
      headers: {
        "Content-Type": "multipart/form-data"
      }
    });
    addNewBrand(result.data);
    return true;
  } catch (error) {
    addError('Failed to create new brand');
    const axiosError = error as AxiosError<{ message?: string }>;
    const message = axiosError?.response?.data?.message;
    if (message) {
      addError(message);
    }
    return false;
  }
}


interface Statistics {
  users: UsersStatistics;
}

export const apiGetStatistics = async () => {
  try {
    const response = await api.get<Statistics>("/brand/statistics");
    setUsersStatistics(response.data.users)
    return true
  } catch (error) {
    addError("Failed to fetch reporting statistics");
    const axiosError = error as AxiosError<{ message?: string }>;
    const message = axiosError?.response?.data?.message;
    if (message) {
      addError(message);
    }
    return false
  }
};

export const apiGetBrandById = async (brandId: string) => {
  try {
    const response = await api.get<Brand>(`/brand/${brandId}`);

    return response.data;
  } catch (error) {
    addError("Failed to fetch reporting statistics");
    const axiosError = error as AxiosError<{ message?: string }>;
    const message = axiosError?.response?.data?.message;
    if (message) {
      addError(message);
    }
    return null;
  }
}

export const apiGetBrandUsers = async (brandId: string) => {
  try {
    const response = await api.get<BrandUser[]>(`/brand/${brandId}/users`);
    return response.data;
  } catch (error) {
    addError("Failed to fetch brand users");
    const axiosError = error as AxiosError<{ message?: string }>;
    const message = axiosError?.response?.data?.message;
    if (message) {
      addError(message);
    }
    return [];
  }
}

export const apiUpsertBrandTheme = async (brandId: string, formData: FormData) => {
  try {
    await api.post(`/brand/${brandId}/theme`, formData, {
      headers: {
        "Content-Type": "multipart/form-data"
      }
    });

  } catch (error) {
    addError("Failed to fetch reporting statistics");
    const axiosError = error as AxiosError<{ message?: string }>;
    const message = axiosError?.response?.data?.message;
    if (message) {
      addError(message);
    }
    return null;
  }
}

export const apiGetBrandThemeUrl = async (brandId: string): Promise<string | null> => {
  try {
    const result = await publicApi.get<{ url: string | null }>(
      `/brand/theme?brandId=${brandId}`
    );
    return result.data.url;
  } catch (error) {
    const axiosError = error as AxiosError<{ message?: string }>;
    const message = axiosError?.response?.data?.message;
    addError(`Failed to fetch brand theme url ${message ? ': ' + message : ''}`);
    return null;
  }
}

export const fetchBrandTheme = async (url: string): Promise<BrandThemeObject | null> => {
  try {
    const res = await fetch(url, {
      // mode: "cors",
      headers: { Accept: "application/json" },
    });
    if (!res.ok) {
      addError("Failed to fetch brand theme");
      console.error(res.body);
      return null;
    }
    const jsonObj = await res.json() as BrandThemeObject;
    return jsonObj;
  } catch (error) {
    addError("Failed to fetch brand theme");
    console.error(error);
    return null;
  }
}

export const fetchFontAsFile = async (url: string, fileName?: string): Promise<File | null> => {
  try {
    const res = await fetch(url, {
      headers: { Accept: "font/ttf, font/otf, font/woff2, font/woff, application/octet-stream" },
    });

    if (!res.ok) {
      console.error(res.statusText);
      addError("Failed to fetch theme font");
      return null;
    }

    let mime = res.headers.get("Content-Type") || "";
    if (!mime || mime === "application/octet-stream") {
      const lower = url.toLowerCase();
      if (lower.endsWith(".ttf")) mime = "font/ttf";
      else if (lower.endsWith(".otf")) mime = "font/otf";
      else if (lower.endsWith(".woff2")) mime = "font/woff2";
      else if (lower.endsWith(".woff")) mime = "font/woff";
      else mime = "application/octet-stream";
    }

    const inferredName = fileName ?? decodeURIComponent(
      url.split("?")[0].split("#")[0].split("/").pop() || "font"
    );

    const blob = await res.blob();

    const file = new File([blob], inferredName, {
      type: mime,
      lastModified: Date.now(),
    });

    return file;
  } catch (error) {
    console.error(error);
    addError("Failed to fetch theme font");
    return null;
  }
}

export const apiResetBrandTheme = async (brandId: string): Promise<boolean> => {
  try {
    await api.delete(`/brand/${brandId}/theme`);
    return true;
  } catch (error) {
    addError("Failed to reset brand theme to default");
    const axiosError = error as AxiosError<{ message?: string }>;
    const message = axiosError?.response?.data?.message;
    if (message) {
      addError(message);
    }
    return false;
  }
}

export const apiUpdateBrand = async (brandId: string, formData: FormData) => {
  try {
    const result = await api.put<Brand>(`/brand/${brandId}`, formData, {
      headers: {
        "Content-Type": "multipart/form-data"
      }
    });
    return result.data;
  } catch (error) {
    console.error(error);
    addError((error as AxiosError<{ message?: string }>)?.response?.data?.message || "Failed to update brand info");
    return null;
  }
}

export const apiUpdateBrandStatus = async (
  brandId: string,
  brand_status: BrandStatus,
): Promise<Brand | null> => {
  try {
    const result = await api.patch<Brand>(`/brand/${brandId}/status`, { brand_status });
    return result.data;
  } catch (error) {
    console.error(error);
    addError(
      (error as AxiosError<{ message?: string }>)?.response?.data?.message ||
      "Failed to update brand status",
    );
    return null;
  }
}

export const apiUpdateBrandRelation = async (brandId: string, parentBrandId: string | null) => {
  try {
    const result = await api.patch(`/brand/${brandId}/relation`, {
      parent_brand_id: parentBrandId
    });
    return result.data;
  } catch (error) {
    console.error(error);
    const axiosError = error as AxiosError<{ message?: string }>;
    const message = axiosError?.response?.data?.message;
    if (message) {
      addError(message);
    } else {
      addError("Failed to update brand relationship");
    }
    return null;
  }
}